// Shared UI glue for the coach-fork update picker — used by exercise-switch,
// edit-exercise-page, and exercise-stats so the diff-build wiring + the field-label
// switch live in ONE place (three call sites = over the duplication threshold).
//
// This module imports `LL` (i18n) and `getEquipmentTemplates`, so it is intentionally NOT
// part of the pure, unit-tested `exercise-diff.ts` / `exercise-fork-update.ts` modules
// (which must stay DI-free / i18n-free for their mocha tests). It's thin presentation glue.

import { CustomExercise } from '@cf-shared/data-model/exercise';
import { LocaleSettings } from '@cf-shared/shared-util';
import { getEquipmentTemplates } from 'pages/equipment/equipment-templates';
import { LL } from 'i18n/i18n.service';
import { buildCoachUpdateDiff, ExerciseDiffEntry, ExerciseDiffField } from './exercise-diff';

// Map a diff field literal to its localized label. Switch-on-literal preserves
// typesafe-i18n's typed accessors (the alternative `(labels as any)[field]` silently
// regresses on a rename); the `never` default errors at compile time if a new
// `ExerciseDiffField` value is added without a case here.
export function fieldDiffLabel(field: ExerciseDiffField): string {
  const labels = LL.editExercise.alerts.updateAvailable.fieldDiffLabels;
  switch (field) {
    case 'name': return labels.name();
    case 'shortname': return labels.shortname();
    case 'description': return labels.description();
    case 'videoUrl': return labels.videoUrl();
    case 'bw_pc': return labels.bw_pc();
    case 'allowMyoReps': return labels.allowMyoReps();
    case 'avoidRMTesting': return labels.avoidRMTesting();
    case 'standard': return labels.standard();
    case 'equipmentTemplateId': return labels.equipmentTemplateId();
    case 'similarExerciseId': return labels.similarExerciseId();
    default: { const _: never = field; return _; }
  }
}

// Build the 3-way coach-update diff with the standard equipment + i18n resolvers. Offers
// only fields the coach changed since the fork base (suppressing the user's own fork-time
// divergences); base = the `coachBaseFields` snapshot on the fork. `resolveExerciseName`
// is caller-supplied because each surface resolves `similarExerciseId` names from a
// different already-loaded list (and falls back to the raw id).
export function buildLabeledCoachUpdateDiff(
  fork: CustomExercise,
  source: CustomExercise,
  locale: LocaleSettings,
  resolveExerciseName: (id: string | undefined) => string,
): ExerciseDiffEntry[] {
  const equipment = getEquipmentTemplates(locale);
  const ua = LL.editExercise.alerts.updateAvailable;
  const resolveEquipmentName = (id: string | undefined) =>
    (id && equipment.find(t => t.id === id)?.name) || ua.empty();
  const base = fork.local?.coachBaseFields as CustomExercise | undefined;
  return buildCoachUpdateDiff(fork, source, base, {
    resolveEquipmentName,
    resolveExerciseName,
    toggledOn: ua.toggledOn(),
    toggledOff: ua.toggledOff(),
    descriptionChanged: ua.descriptionChanged(),
    standardChanged: ua.standardChanged(),
    empty: ua.empty(),
  });
}
